import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { ShieldCheck } from "lucide-react";
import { getBusinessAssistants } from "../../services/eventDetailService";
import { extractListResult } from "../../utils/apiHelpers";
import Modal from "../common/Modal";
import Loader from "../common/Loader";
import EmptyState from "../common/EmptyState";
import EditPermissionsModal from "./EditPermissionsModal";
import { color } from "../../styles/theme";

const getUserImage = (u) =>
  u?.AppUser?.BusinessDetails?.LogoUrl || u?.LogoUrl || u?.DisplayPicture || null;

const getDisplayName = (u, fallback) =>
  u?.BusinessNickName || u?.AssistantName || u?.AppUser?.BusinessDetails?.BusinessNickName || u?.NickName || fallback;

/**
 * "BA Permission" — the Settings row of the same name previously just
 * navigated to the Participants tab (its per-user permissions editor only
 * opens from there). This gives Settings its own real entry point: list the
 * event's Business Assistants and open the same EditPermissionsModal for
 * whichever one is picked.
 */
const BusinessAssistantPermissionsModal = ({ eventId, onClose }) => {
  const { t } = useTranslation(["eventDetails", "common"]);
  const [assistants, setAssistants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [permissionsTarget, setPermissionsTarget] = useState(null);

  const load = () => {
    setLoading(true);
    getBusinessAssistants(eventId, { pageNumber: 1, pageSize: 50 })
      .then((res) => setAssistants(extractListResult(res.data)))
      .catch(() => toast.error(t("participantsTab.loadAssistantsError")))
      .finally(() => setLoading(false));
  };

  useEffect(load, [eventId]);

  return (
    <Modal title={t("baPermissionsModal.title")} subtitle={t("baPermissionsModal.subtitle")} onClose={onClose} maxWidth={440}>
      {loading ? (
        <Loader />
      ) : assistants.length === 0 ? (
        <EmptyState icon={ShieldCheck} text={t("baPermissionsModal.empty")} />
      ) : (
        <div style={s.list}>
          {assistants.map((u) => {
            const id = u.AssistantId || u.Id;
            const name = getDisplayName(u, t("participantsTab.defaultNames.unnamed"));
            const image = getUserImage(u);
            return (
              <div key={id} style={s.row}>
                {image ? <img src={image} alt="" style={s.avatar} /> : <div style={s.avatarFallback}>{name.charAt(0).toUpperCase()}</div>}
                <span style={s.name}>{name}</span>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setPermissionsTarget(u)}>
                  {t("participantsTab.actions.permissions")}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {permissionsTarget && (
        <EditPermissionsModal
          assistant={permissionsTarget}
          onClose={() => setPermissionsTarget(null)}
          onUpdated={() => { setPermissionsTarget(null); load(); }}
        />
      )}
    </Modal>
  );
};

const s = {
  list: { display: "flex", flexDirection: "column", gap: 10 },
  row: { display: "flex", alignItems: "center", gap: 10, padding: "6px 0" },
  avatar: { width: 32, height: 32, borderRadius: "50%", objectFit: "cover", flexShrink: 0 },
  avatarFallback: {
    width: 32, height: 32, borderRadius: "50%", background: color.primary50, color: color.primary600,
    display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, flexShrink: 0,
  },
  name: { fontSize: 13.5, color: color.textPrimary, fontWeight: 500, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
};

export default BusinessAssistantPermissionsModal;
